import { motion } from "framer-motion";
import { Link } from "react-router-dom";

const Hero = () => {
  return (
    <div className="relative min-h-screen bg-[#0A0A0A] overflow-hidden">
      {/* Geometric Background Patterns */}
      <div className="absolute inset-0 opacity-[0.03]">
        <div className="art-deco-pattern h-full w-full"></div>
      </div>

      {/* Decorative Corner Elements */}
      <div className="absolute top-0 left-0 w-[300px] h-[300px] art-deco-corner opacity-10"></div>
      <div className="absolute top-0 right-0 w-[300px] h-[300px] art-deco-corner rotate-90 opacity-10"></div>
      <div className="absolute bottom-0 left-0 w-[300px] h-[300px] art-deco-corner -rotate-90 opacity-10"></div>
      <div className="absolute bottom-0 right-0 w-[300px] h-[300px] art-deco-corner rotate-180 opacity-10"></div>

      <div className="relative container mx-auto px-6 py-8">
        {/* Navigation */}
        <motion.nav
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8 }}
          className="desktop:flex laptop:flex tablet:flex mobile:hidden items-center justify-between mb-20"
        >
          <Link to="/" className="flex items-center">
            <div className="art-deco-logo">
              <span className="text-[#D4AF37] text-3xl tracking-[0.4em] font-extralight">
                COIFFEUR
              </span>
            </div>
          </Link>

          <div className="flex items-center gap-16">
            <NavLink text="SERVICES" />
            <NavLink text="GALLERY" />
            <NavLink text="ABOUT" />
            <NavLink text="CONTACT" />
          </div>
        </motion.nav>

        {/* Main Content */}
        <div className="flex desktop:flex-row laptop:flex-row tablet:flex-col mobile:flex-col items-center justify-between min-h-[80vh] gap-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="desktop:w-[45%] laptop:w-[45%] tablet:w-full mobile:w-full"
          >
            <div className="art-deco-container p-20 relative">
              <div className="relative z-10">
                <div className="flex items-center gap-6 mb-10">
                  <div className="art-deco-line"></div>
                  <span className="text-[#D4AF37] tracking-[0.5em] text-sm font-light">
                    SINCE 1925
                  </span>
                </div>

                <h1 className="desktop:text-7xl laptop:text-6xl tablet:text-5xl mobile:text-4xl font-extralight text-white mb-8 tracking-wider leading-tight">
                  Coucou
                  <span className="font-light text-[#D4AF37] art-deco-heading mt-4 block">
                    THOMAS
                  </span>
                </h1>

                <p className="text-gray-300 desktop:text-lg laptop:text-lg tablet:text-base mobile:text-base leading-relaxed mb-16 font-light tracking-wide">
                  Experience the pinnacle of luxury hair artistry in our art
                  deco sanctuary. Where timeless sophistication meets
                  contemporary excellence.
                </p>

                <div className="flex items-center gap-12">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="book-button px-14 py-6 relative overflow-hidden group"
                  >
                    <span className="relative z-10 text-black font-light tracking-[0.3em] text-sm">
                      BOOK NOW
                    </span>
                  </motion.button>

                  <div className="flex items-center gap-6">
                    <div className="art-deco-separator"></div>
                    <span className="text-[#D4AF37] tracking-[0.4em] text-sm font-light">
                      PARIS
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Image Section */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="desktop:w-[55%] laptop:w-[55%] tablet:w-full mobile:w-full"
          >
            <div className="art-deco-frame">
              <img
                src="https://placehold.co/900x1200/1a1a1a/D4AF37?text=COIFFEUR"
                alt="Luxury Salon"
                className="w-full h-auto object-cover"
              />
            </div>
          </motion.div>
        </div>

        {/* Bottom Decorative Element */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="absolute bottom-12 left-0 w-full"
        >
          <div className="flex justify-center">
            <div className="art-deco-footer"></div>
          </div>
        </motion.div>
      </div>

      <style>
        {`
          .art-deco-pattern {
            background-image: 
              linear-gradient(30deg, rgba(212, 175, 55, 0.1) 1px, transparent 1px),
              linear-gradient(-30deg, rgba(212, 175, 55, 0.1) 1px, transparent 1px);
            background-size: 80px 80px;
          }

          .art-deco-corner {
            background-image: 
              linear-gradient(45deg, #D4AF37 1px, transparent 1px),
              linear-gradient(-45deg, #D4AF37 1px, transparent 1px);
            background-size: 30px 30px;
            clip-path: polygon(0 0, 100% 0, 0 100%);
          }

          .art-deco-container {
            background: linear-gradient(135deg, rgba(212, 175, 55, 0.05), transparent);
            border: 1px solid rgba(212, 175, 55, 0.2);
            position: relative;
          }

          .art-deco-container::before,
          .art-deco-container::after {
            content: '';
            position: absolute;
            width: calc(100% + 20px);
            height: calc(100% + 20px);
            border: 1px solid rgba(212, 175, 55, 0.1);
            pointer-events: none;
          }

          .art-deco-container::before {
            top: -20px;
            left: -20px;
          }

          .art-deco-container::after {
            bottom: -20px;
            right: -20px;
          }

          .art-deco-heading {
            background: linear-gradient(to right, #D4AF37, #E5C158);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            position: relative;
          }

          .book-button {
            background: linear-gradient(45deg, #D4AF37, #E5C158);
            transition: all 0.4s ease;
            border: 1px solid rgba(212, 175, 55, 0.3);
          }

          .book-button::before {
            content: '';
            position: absolute;
            inset: 0;
            background: linear-gradient(-45deg, #E5C158, #D4AF37);
            opacity: 0;
            transition: opacity 0.4s ease;
          }

          .book-button:hover::before {
            opacity: 1;
          }

          .art-deco-line {
            width: 80px;
            height: 1px;
            background: linear-gradient(to right, #D4AF37, transparent);
          }

          .art-deco-separator {
            position: relative;
            width: 40px;
            height: 1px;
            background: #D4AF37;
          }

          .art-deco-separator::before,
          .art-deco-separator::after {
            content: '';
            position: absolute;
            width: 8px;
            height: 8px;
            background: #D4AF37;
            transform: rotate(45deg);
            top: -3.5px;
          }

          .art-deco-separator::before {
            left: -4px;
          }

          .art-deco-separator::after {
            right: -4px;
          }

          .art-deco-frame {
            position: relative;
            padding: 30px;
            border: 1px solid rgba(212, 175, 55, 0.2);
            background: linear-gradient(45deg, rgba(212, 175, 55, 0.03), transparent);
          }

          .art-deco-frame::before {
            content: '';
            position: absolute;
            inset: -2px;
            background: linear-gradient(45deg, #D4AF37, transparent);
            z-index: -1;
            opacity: 0.1;
          }

          .art-deco-logo {
            position: relative;
            padding: 15px 30px;
          }

          .art-deco-logo::before,
          .art-deco-logo::after {
            content: '';
            position: absolute;
            width: 30px;
            height: 1px;
            background: #D4AF37;
            top: 50%;
          }

          .art-deco-logo::before {
            left: -15px;
            transform: translateY(-50%) rotate(-45deg);
          }

          .art-deco-logo::after {
            right: -15px;
            transform: translateY(-50%) rotate(45deg);
          }

          .art-deco-footer {
            width: 300px;
            height: 1px;
            background: linear-gradient(to right, 
              transparent, 
              rgba(212, 175, 55, 0.3) 20%, 
              rgba(212, 175, 55, 0.6) 50%,
              rgba(212, 175, 55, 0.3) 80%,
              transparent
            );
          }
        `}
      </style>
    </div>
  );
};

const NavLink = ({ text }: { text: string }) => (
  <motion.span
    whileHover={{ scale: 1.05 }}
    className="text-white/60 hover:text-[#D4AF37] transition-colors cursor-pointer tracking-[0.3em] text-sm font-extralight"
  >
    {text}
  </motion.span>
);

export default Hero;
